import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from '@tanstack/react-router';
import { CenteredColumn } from '@/components/CenteredColumn';
import { Section } from '@/components/Section';
import { DocumentHead } from '@/components/core/DocumentHead';
import { WarningBanner } from '@/components/core/WarningBanner';
import { useFavicon } from '@/hooks/useFavicon';
import { IconExternalLink } from '@/icons';

interface ScheduledDowntimePageProps {
  tenantId?: string;
}

export const ScheduledDowntimePage = ({
  tenantId,
}: ScheduledDowntimePageProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useFavicon();

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <>
      <DocumentHead
        titleKey="page.downtime.title"
        descriptionKey="page.downtime.description"
        appendAppName={false}
      />
      <main className="flex h-screen w-full">
        <CenteredColumn className="justify-center">
          <Section className="m-auto max-w-lg flex-col gap-4">
            <WarningBanner>
              <FormattedMessage defaultMessage="Portalen är tillfälligt stängd" />
            </WarningBanner>
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="text-2xl font-medium text-text-950"
            >
              <FormattedMessage defaultMessage="Planerat underhåll" />
            </h1>
            <p>
              <FormattedMessage defaultMessage="Portalen är stängd måndagen den 26 oktober 2026 på grund av en planerad systemuppdatering. Det går inte att logga in eller använda portalen under denna dag, varken som administratör eller i självbetjäning." />
            </p>
            <p>
              <FormattedMessage defaultMessage="Tjänsten beräknas vara tillgänglig igen tisdagen den 27 oktober 2026." />
            </p>
            <p>
              <FormattedMessage defaultMessage="Tjänstaresa i app och på kort fortsätter att fungera under tiden." />
            </p>
            <a
              href="/accessibility"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Tillgänglighetsredogörelse (öppnas i ny flik)"
              className="mx-auto mt-4 inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-text-950 hover:bg-background-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600"
            >
              <FormattedMessage defaultMessage="Tillgänglighetsredogörelse" />
              <IconExternalLink className="ml-2 size-4" aria-hidden="true" />
            </a>
            {tenantId && (
              <Link
                to="/$tenantId/login"
                params={{ tenantId }}
                className="mx-auto inline-flex items-center justify-center rounded-md px-4 py-2 text-xs text-text-700 hover:bg-background-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600"
              >
                <FormattedMessage defaultMessage="Logga in som Allbinary-personal" />
              </Link>
            )}
          </Section>
        </CenteredColumn>
      </main>
    </>
  );
};
